import { create } from "zustand";
import { api } from "@/lib/api";
import type {
  ReadMessageFeedbackDto,
  ReadMessageWithSourcesDto
} from "@package/types";
import type { AsyncState } from "./types";

interface ChatMessages extends AsyncState {
  messages: ReadMessageWithSourcesDto[];
}

interface MessagesState {
  messagesByChat: Record<string, ChatMessages>;
  currentChatId: string | null;
}

interface MessagesActions {
  loadMessages: (chatId: string, force?: boolean) => Promise<void>;
  setCurrentChatId: (chatId: string) => void;
  addMessage: (message: ReadMessageWithSourcesDto) => void;
  removeMessage: (chatId: string, id: number) => void;
  setMessageFeedback: (
    chatId: string,
    messageId: number,
    feedback: ReadMessageFeedbackDto | null
  ) => void;
  reset: () => void;
}

type MessagesStore = MessagesState & MessagesActions;

export const useMessagesStore = create<MessagesStore>()((set, get) => ({
  messagesByChat: {},
  currentChatId: null,

  loadMessages: async (chatId: string, force = false) => {
    const existing = get().messagesByChat[chatId];

    if (!force && (existing?.isLoaded || existing?.isLoading)) {
      set({ currentChatId: chatId });
      return;
    }

    if (force) {
      // keep the current list visible while refetching
      set({ currentChatId: chatId });
    } else {
      set((state) => ({
        currentChatId: chatId,
        messagesByChat: {
          ...state.messagesByChat,
          [chatId]: {
            messages: [],
            isLoading: true,
            isLoaded: false,
            error: null
          }
        }
      }));
    }

    try {
      const data = await api.get<ReadMessageWithSourcesDto[]>(
        `/chats/${chatId}/messages`
      );
      // a poll that brings nothing new must not replace the array, or every
      // consumer re-renders and the auto-scroll effect fires
      const current = get().messagesByChat[chatId]?.messages;
      const unchanged =
        force &&
        current?.length === data.length &&
        current.at(-1)?.id === data.at(-1)?.id;

      if (get().currentChatId === chatId && !unchanged) {
        set((state) => ({
          messagesByChat: {
            ...state.messagesByChat,
            [chatId]: {
              messages: data,
              isLoading: false,
              isLoaded: true,
              error: null
            }
          }
        }));
      }
    } catch (error) {
      if (get().currentChatId === chatId) {
        set((state) => ({
          messagesByChat: {
            ...state.messagesByChat,
            [chatId]: {
              messages: state.messagesByChat[chatId]?.messages ?? [],
              isLoaded: state.messagesByChat[chatId]?.isLoaded ?? false,
              isLoading: false,
              error: (error as Error).message
            }
          }
        }));
      }
    }
  },

  setCurrentChatId: (chatId: string) => set({ currentChatId: chatId }),

  addMessage: (message: ReadMessageWithSourcesDto) =>
    set((state) => {
      const chatId = message.chatId;

      const chat = state.messagesByChat[chatId] ?? {
        messages: [],
        isLoading: false,
        isLoaded: true,
        error: null
      };

      return {
        messagesByChat: {
          ...state.messagesByChat,
          [chatId]: { ...chat, messages: [...chat.messages, message] }
        }
      };
    }),

  removeMessage: (chatId: string, id: number) =>
    set((state) => {
      const chat = state.messagesByChat[chatId];
      if (!chat) return state;

      return {
        messagesByChat: {
          ...state.messagesByChat,
          [chatId]: {
            ...chat,
            messages: chat.messages.filter((m) => m.id !== id)
          }
        }
      };
    }),

  setMessageFeedback: (
    chatId: string,
    messageId: number,
    feedback: ReadMessageFeedbackDto | null
  ) =>
    set((state) => {
      const chat = state.messagesByChat[chatId];
      if (!chat) return state;

      return {
        messagesByChat: {
          ...state.messagesByChat,
          [chatId]: {
            ...chat,
            messages: chat.messages.map((m) =>
              m.id === messageId ? { ...m, feedback } : m
            )
          }
        }
      };
    }),

  reset: () => set({ messagesByChat: {}, currentChatId: null })
}));
